/* WebNest — minimal Word-style rich text editor.
   A contentEditable surface with a formatting toolbar (headings, bold/italic/
   underline, lists, links). Outputs HTML via onChange; the client Help page
   renders that HTML (sanitised with DOMPurify). No external dependency — uses
   document.execCommand, which is deprecated but universally supported and fine
   for an internal admin editor. */
import React, { useRef, useEffect } from 'react'
import { I } from './ns-icons'

export default function RichTextEditor({ value, onChange, placeholder }) {
  const ref = useRef(null)

  // Seed the initial HTML once; afterwards the surface is uncontrolled so the
  // caret isn't reset on every keystroke.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fire = () => onChange(ref.current ? ref.current.innerHTML : '')
  const run = (cmd, arg = null) => {
    ref.current?.focus()
    document.execCommand(cmd, false, arg)
    fire()
  }
  const block = (tag) => run('formatBlock', `<${tag}>`)
  const link = () => {
    const url = window.prompt('Link URL', 'https://')
    if (url) run('createLink', url)
  }

  const Btn = ({ onClick, title, children, wide }) => (
    <button
      type="button"
      className="rte-btn"
      title={title}
      style={wide ? { minWidth: 30 } : undefined}
      onMouseDown={(e) => e.preventDefault()} // keep the selection while clicking
      onClick={onClick}
    >{children}</button>
  )

  return (
    <div className="rte">
      <div className="rte-toolbar">
        <Btn title="Paragraph" onClick={() => block('p')} wide>¶</Btn>
        <Btn title="Heading" onClick={() => block('h2')} wide>H2</Btn>
        <Btn title="Subheading" onClick={() => block('h3')} wide>H3</Btn>
        <span className="rte-sep" />
        <Btn title="Bold" onClick={() => run('bold')}><b>B</b></Btn>
        <Btn title="Italic" onClick={() => run('italic')}><i>I</i></Btn>
        <Btn title="Underline" onClick={() => run('underline')}><u>U</u></Btn>
        <span className="rte-sep" />
        <Btn title="Bulleted list" onClick={() => run('insertUnorderedList')}><I.list /></Btn>
        <Btn title="Numbered list" onClick={() => run('insertOrderedList')} wide>1.</Btn>
        <Btn title="Add link" onClick={link}><I.link /></Btn>
        <Btn title="Clear formatting" onClick={() => run('removeFormat')}><I.x /></Btn>
      </div>
      <div
        ref={ref}
        className="rte-body"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder || 'Write here…'}
        onInput={fire}
        onBlur={fire}
      />
    </div>
  )
}
